import React, { memo, useEffect, useMemo } from 'react'
import { UseFormMethods, useWatch } from 'react-hook-form'
import { IBidRateCoachGrades } from 'types/graphqlTypes'
import CarrierOptionRateCard from 'apps/marketplace/components/CarrierOptionRateCard'
import { useShipmentRates } from 'apps/marketplace/components/CarrierOptionRateCard/hooks'
import { TCarrierOptionSchema } from '../../schema'
import CounterOfferFormSection from '../CounterOfferFormSection'
import { useSuggestedCounterAmountQuery } from './graphql/SuggestedCounterAmount'

export interface IRateCardFormSectionProps
  extends Pick<UseFormMethods, 'control' | 'setValue' | 'errors'> {
  shipmentId: string
  isEditMode: boolean
}

const RateCardFormSection = memo<IRateCardFormSectionProps>(
  ({ control, errors, isEditMode, setValue, shipmentId }) => {
    // Carrier
    const carrierApplication = useWatch<TCarrierOptionSchema['carrierApplication']>({
      control,
      name: 'carrierApplication',
    })
    const { id: carrierId } = carrierApplication || {}

    // Dispatcher Id
    const dispatcherId = useWatch<TCarrierOptionSchema['carrierContactUserId']>({
      control,
      name: 'carrierContactUserId',
    })
    const isKnownDispatcher = Boolean(dispatcherId)

    // Rate
    const rate = useWatch<TCarrierOptionSchema['amount']>({ control, name: 'amount' })

    // Non-Bookable
    const isNonBookable = useWatch<TCarrierOptionSchema['nonBookable']>({
      control,
      name: 'nonBookable',
    })

    const { rateCoachGrade } = useShipmentRates({ rate, shipmentId })
    const { data } = useSuggestedCounterAmountQuery({
      variables: { shipmentId },
    })
    const { shipment } = data || {}
    const { suggestedCounterAmount } = shipment || {}

    /** Show Counter Offer card if rate is fair or poor */
    const showCounterOfferFormSection = useMemo(() => {
      if (!rateCoachGrade || isNonBookable) {
        return false
      }

      return [IBidRateCoachGrades.fair, IBidRateCoachGrades.poor].includes(rateCoachGrade)
    }, [isNonBookable, rateCoachGrade])

    /**
     * This useEffect sets counterOfferAmount to the suggestedCounterAmount
     * if the rate has a grade of fair or poor and the form is not in editMode.
     * Otherwise, counterOfferAmount is set to null.
     */
    useEffect(() => {
      if (showCounterOfferFormSection) {
        if (isKnownDispatcher && !isEditMode) {
          setValue('counterOfferAmount', suggestedCounterAmount)
        }
      } else {
        setValue('counterOfferAmount', null)
      }
    }, [
      isEditMode,
      isKnownDispatcher,
      setValue,
      showCounterOfferFormSection,
      suggestedCounterAmount,
    ])

    return (
      <CarrierOptionRateCard
        shipmentId={shipmentId}
        carrierApplicationId={carrierId}
        rate={rate}
        counterOfferComponent={
          <>
            {showCounterOfferFormSection && (
              <CounterOfferFormSection
                control={control}
                name='counterOfferAmount'
                errors={errors}
                setValue={setValue}
              />
            )}
          </>
        }
      />
    )
  }
)

export default RateCardFormSection
